import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { window as neutralinoWindow } from "@neutralinojs/lib";

interface NeutralinoContextValue {
  isAlwaysOnTop: boolean;
  toggleAlwaysOnTop: () => void;
}

const NeutralinoContext = createContext<NeutralinoContextValue>({
  isAlwaysOnTop: true,
  toggleAlwaysOnTop: () => {},
});

export const useNeutralino = () => useContext(NeutralinoContext);

export const NeutralinoProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAlwaysOnTop, setIsAlwaysOnTop] = useState(true);

  useEffect(() => {
    // Set initial alwaysOnTop state to true on launch
    neutralinoWindow.setAlwaysOnTop(true);
  }, []);

  const toggleAlwaysOnTop = useCallback(async () => {
    const newState = !isAlwaysOnTop;
    await neutralinoWindow.setAlwaysOnTop(newState);
    setIsAlwaysOnTop(newState);
  }, [isAlwaysOnTop]);

  const value = useMemo(() => {
    return { isAlwaysOnTop, toggleAlwaysOnTop };
  }, [isAlwaysOnTop, toggleAlwaysOnTop]);

  return (
    <NeutralinoContext.Provider value={value}>
      {children}
    </NeutralinoContext.Provider>
  );
};
